"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../../ui/button";
import { MultipleChoiceProps, QuizOption } from "../type";
import Image from "next/image";
import { processDynamicText } from "../utils/processDynamicText";
import { useInView } from "react-intersection-observer";
import makeEvent from "@/hooks/eventing/makeEvent";
import { FormViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.FormViewed";
import { FieldSelected } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldSelected";
import { FieldViewed } from "@/hooks/eventing/types/core.fieldViewed.v1";
import { FormContinued } from "@/hooks/eventing/types/redventures.usertracking.v3.FormContinued";
import { FormStarted } from "@/hooks/eventing/types/redventures.usertracking.v3.FormStarted";
import { FormSubmitted } from "@/hooks/eventing/types/redventures.usertracking.v3.FormSubmitted";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { EVENTS } from "@/hooks/eventing/types/events";
import { v4 as uuid } from "uuid";
import { SkeletonLoader } from "./SkeletonLoader";
import { OptionsContainer } from "./OptionsContainer";
import { PaginationDots } from "./PaginationDots";
import { ShowAllButton } from "./ShowAllButton";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { VARIANT_TYPES } from "../type";
import { InlineQuizVariant } from "./InlineQuizVariant";
import { CompactQuizVariant } from "./CompactQuizVariant";
import useTranslations from "@/i18n/useTranslations";

const MultipleChoice: React.FC<MultipleChoiceProps> = ({
  section,
  dynamicOptions,
  currentAnswer,
  onAnswer,
  onBack,
  onNext,
  totalSections,
  currentSectionIndex,
  allAnswers = {},
  quizConfig,
  isLoadingOptions = false,
  optionsDisplayMode = "search",
  variant = VARIANT_TYPES.default,
}) => {
  const tCommon = useTranslations("Common");
  const t = useTranslations("GuidedPath");
  const tCourse = useTranslations("Course");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [showAllOptions, setShowAllOptions] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [liveMessage, setLiveMessage] = useState<string>("");
  const viewedRef = useRef(false);
  const formStartedRef = useRef(false);
  const correlationIdRef = useRef(uuid());
  const fieldViewedRef = useRef(false);
  const showAllViewedRef = useRef(false);
  const showLessViewedRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const hasMountedRef = useRef(false);
  const previousSectionIndexRef = useRef<number>(currentSectionIndex);

  const sectionTitle =
    section?.variantTitles?.[variant] || section?.title || "";

  const buildFormEventData = useCallback(
    () => ({
      correlationId: correlationIdRef.current,
      formContext: {
        formId: "find-your-path",
        formName: "find-your-path",
        formType: "flow" as const,
      },
      stepContext: {
        stepName: section.stepName,
        stepNumber: currentSectionIndex,
      },
    }),
    [section.stepName, currentSectionIndex],
  );

  const buildFieldEventData = useCallback(
    (value: string, label: string) => ({
      correlationId: correlationIdRef.current,
      formContext: {
        formId: "find-your-path",
        formName: "find-your-path",
        formType: "flow" as const,
      },
      userInputField: {
        fieldName: sectionTitle,
        fieldType:
          section.selectionType === "single"
            ? ("single-choice" as const)
            : ("multiple-choice" as const),
        fieldValue: value,
        fieldLabel: label,
        fieldNumber: currentSectionIndex,
      },
      stepContext: section.stepName
        ? {
            stepName: section.stepName,
            stepNumber: currentSectionIndex,
          }
        : undefined,
    }),
    [
      sectionTitle,
      section.selectionType,
      section.stepName,
      currentSectionIndex,
    ],
  );

  const fireEvent = useCallback(<T,>(eventData: T, eventType: EVENTS) => {
    const event = makeEvent<T, EVENTS>(eventData);
    const eventHandler = event<T>(eventType);
    eventHandler();
  }, []);

  const processedTitle = quizConfig
    ? processDynamicText(sectionTitle, allAnswers, quizConfig)
    : sectionTitle;
  const processedDescription = quizConfig
    ? processDynamicText(section.description, allAnswers, quizConfig)
    : section.description;

  // Form view tracking
  const { ref: quizRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && !viewedRef.current && currentSectionIndex === 0) {
      fireEvent<FormViewed>(buildFormEventData(), EVENTS.FormViewed);
      viewedRef.current = true;
    }
  }, [inView, currentSectionIndex, buildFormEventData, fireEvent]);

  // Reset tracking when section changes
  useEffect(() => {
    setSearchText("");
    setShowAllOptions(false);
    setShowDropdown(false);
    viewedRef.current = false;
    fieldViewedRef.current = false;
    showAllViewedRef.current = false;
    showLessViewedRef.current = false;
  }, [currentSectionIndex]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousSectionIndexRef.current = currentSectionIndex;
      return;
    }

    const sectionChanged =
      previousSectionIndexRef.current !== currentSectionIndex;

    if (sectionChanged && headingRef.current) {
      headingRef.current.focus({ preventScroll: true });
    }

    previousSectionIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  useEffect(() => {
    const safeTitle =
      processedTitle || sectionTitle || `Step ${currentSectionIndex + 1}`;
    const nextMessage = `Step ${currentSectionIndex + 1} of ${totalSections}: ${safeTitle}`;
    setLiveMessage((prevMessage) =>
      prevMessage === nextMessage ? prevMessage : nextMessage,
    );
  }, [currentSectionIndex, totalSections, processedTitle, sectionTitle]);

  // Update selected options when current answer changes
  useEffect(() => {
    if (currentAnswer) {
      const answerArray = Array.isArray(currentAnswer)
        ? currentAnswer
        : [currentAnswer];
      setSelectedOptions(answerArray);
    } else {
      setSelectedOptions([]);
    }
  }, [currentAnswer]);

  const fireFieldViewed = useCallback(
    (label: string) => {
      fireEvent<FieldViewed>(
        buildFieldEventData(label, label),
        EVENTS.FieldViewed,
      );
    },
    [buildFieldEventData, fireEvent],
  );

  const fireFieldSelected = useCallback(
    (value: string, label: string) => {
      fireEvent<FieldSelected>(
        buildFieldEventData(value, label),
        EVENTS.FieldSelected,
      );
    },
    [buildFieldEventData, fireEvent],
  );

  const buildElementEventData = useCallback(
    (elementName: string, elementText: string) => ({
      correlationId: correlationIdRef.current,
      webElement: {
        elementType: "button",
        name: elementName,
        text: elementText,
        location: "find-your-path-quiz",
      },
    }),
    [],
  );

  const fireElementViewed = useCallback(
    (elementName: string, elementText: string) => {
      fireEvent<ElementViewed>(
        buildElementEventData(elementName, elementText),
        EVENTS.ElementViewed,
      );
    },
    [buildElementEventData, fireEvent],
  );

  const fireElementClicked = useCallback(
    (elementName: string, elementText: string) => {
      fireEvent<ElementClicked>(
        buildElementEventData(elementName, elementText),
        EVENTS.ElementClicked,
      );
    },
    [buildElementEventData, fireEvent],
  );

  // Track "Show all" button view (expand mode only)
  const { ref: showAllRef, inView: showAllInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (
      showAllInView &&
      !showAllViewedRef.current &&
      dynamicOptions &&
      optionsDisplayMode === "expand"
    ) {
      fireElementViewed("show-all-options", "Show all options");
      showAllViewedRef.current = true;
    }
  }, [showAllInView, dynamicOptions, optionsDisplayMode, fireElementViewed]);

  // Track "Show less" button view (expand mode only)
  const { ref: showLessRef, inView: showLessInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (
      showLessInView &&
      !showLessViewedRef.current &&
      dynamicOptions &&
      optionsDisplayMode === "expand"
    ) {
      fireElementViewed("show-less-options", "Show less options");
      showLessViewedRef.current = true;
    }
  }, [showLessInView, dynamicOptions, optionsDisplayMode, fireElementViewed]);

  // Track field (question title) view
  const { ref: fieldRef, inView: fieldInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (fieldInView && !fieldViewedRef.current) {
      fireFieldViewed(processedTitle);
      fieldViewedRef.current = true;
    }
  }, [fieldInView, processedTitle, fireFieldViewed]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Determine which options to display in main container
  const getDisplayOptions = () => {
    const allOptions = dynamicOptions || section.options || [];

    if (optionsDisplayMode === "search") {
      if (allOptions.length <= 10) {
        return allOptions;
      }

      // Search mode: show selected options from dropdown (beyond first 10) at top,
      // then show first 10 options in their original positions
      const selectedSet = new Set(selectedOptions);
      const dropdownSelections: QuizOption[] = [];
      const first10: QuizOption[] = [];

      // Separate dropdown selections (beyond first 10) and first 10 options
      allOptions.forEach((option, index) => {
        const optionValue = option.value || option.label;
        if (index < 10) {
          // Always include first 10 options
          first10.push(option);
        } else if (selectedSet.has(optionValue)) {
          // Only include selected options beyond first 10 (dropdown selections)
          dropdownSelections.push(option);
        }
      });

      // Return dropdown selections first, then first 10 options
      return [...dropdownSelections, ...first10];
    } else if (optionsDisplayMode === "expand" && !showAllOptions) {
      // Expand mode: show limited options if not expanded
      return allOptions.slice(0, 10);
    }

    // Show all options
    return allOptions;
  };

  const displayedOptions = getDisplayOptions();

  // Get filtered options for dropdown (exclude options already displayed)
  const getFilteredDropdownOptions = () => {
    const allOptions = dynamicOptions || section.options || [];
    if (!searchText) return [];

    const displayedValues = displayedOptions.map(
      (opt) => opt.value || opt.label,
    );

    return allOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase()) &&
        !displayedValues.includes(option.value || option.label),
    );
  };

  const filteredDropdownOptions = getFilteredDropdownOptions();

  // Handle selecting option from dropdown
  const handleDropdownSelect = (option: QuizOption) => {
    const value = option.value || option.label;
    const label = option.label;
    const isSingleSelection = section.selectionType === "single";

    const newSelectedOptions = isSingleSelection
      ? [value]
      : [...selectedOptions, value];

    // Fire FormStarted event on the first field selection of the first step
    if (
      currentSectionIndex === 0 &&
      selectedOptions.length === 0 &&
      !formStartedRef.current
    ) {
      fireEvent<FormStarted>(buildFormEventData(), EVENTS.FormStarted);
      formStartedRef.current = true;
    }

    setSelectedOptions(newSelectedOptions);
    fireFieldSelected(value, label);

    // Clear search and close dropdown
    setSearchText("");
    setShowDropdown(false);

    // Auto-advance ONLY for single selection mode with a small delay
    if (isSingleSelection && section.selectionType === "single") {
      setTimeout(() => {
        processAnswer(newSelectedOptions);
      }, 500);
    }
  };

  const processAnswer = (optionsToProcess: string[]) => {
    const isLastStep = currentSectionIndex === totalSections - 1;

    // Fire FormSubmitted on last step, FormContinued otherwise
    if (isLastStep) {
      fireEvent<FormSubmitted>(buildFormEventData(), EVENTS.FormSubmitted);
    } else {
      fireEvent<FormContinued>(buildFormEventData(), EVENTS.FormContinued);
    }

    const answer =
      section.selectionType === "single"
        ? optionsToProcess[0]
        : optionsToProcess;
    onAnswer(answer);
    onNext(answer);
  };

  const handleOptionToggle = (value: string, label: string) => {
    const isSingleSelection = section.selectionType === "single";

    const newSelectedOptions = isSingleSelection
      ? [value] // Single selection: replace current selection
      : selectedOptions.includes(value)
        ? selectedOptions.filter((option) => option !== value)
        : [...selectedOptions, value];

    // Fire FormStarted event on the first field selection of the first step
    if (
      currentSectionIndex === 0 &&
      selectedOptions.length === 0 &&
      !formStartedRef.current
    ) {
      fireEvent<FormStarted>(buildFormEventData(), EVENTS.FormStarted);
      formStartedRef.current = true;
    }

    setSelectedOptions(newSelectedOptions);

    // Fire field selected event when option is selected (not deselected)
    if (!selectedOptions.includes(value)) {
      fireFieldSelected(value, label);
    }

    // Auto-advance ONLY for single selection mode with a small delay
    if (isSingleSelection && section.selectionType === "single") {
      setTimeout(() => {
        processAnswer(newSelectedOptions);
      }, 500);
    }
  };

  const handleBack = () => {
    fireEvent<FormContinued>(buildFormEventData(), EVENTS.FormContinued);
    onBack();
  };

  const handleNext = () => {
    processAnswer(selectedOptions);
  };

  // Inline variant
  if (variant === VARIANT_TYPES.inline) {
    return (
      <InlineQuizVariant
        title={processedTitle}
        options={displayedOptions}
        selectedOptions={selectedOptions}
        optionsVariant={section.optionsVariant || "basic"}
        selectionType={section.selectionType}
        isLoading={isLoadingOptions}
        liveMessage={liveMessage}
        onToggle={handleOptionToggle}
        quizRef={quizRef}
        fieldRef={fieldRef}
        headingRef={headingRef}
      />
    );
  }

  // Compact variant
  if (variant === VARIANT_TYPES.compact) {
    return (
      <CompactQuizVariant
        title={processedTitle}
        description={processedDescription}
        options={displayedOptions}
        selectedOptions={selectedOptions}
        optionsVariant={section.optionsVariant || "basic"}
        selectionType={section.selectionType}
        isLoading={isLoadingOptions}
        liveMessage={liveMessage}
        onToggle={handleOptionToggle}
        quizRef={quizRef}
        fieldRef={fieldRef}
        headingRef={headingRef}
      />
    );
  }

  // Default variant - full quiz UI
  return (
    <div
      ref={quizRef}
      className="container font-sans rounded-xl flex flex-col gap-4 md:gap-6 p-4 md:p-8 bg-putty-300"
    >
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveMessage}
      </div>
      <div className="flex flex-col text-start">
        <div ref={fieldRef} className="flex items-center gap-2">
          <Image
            src="/icons/ai-icon.png"
            alt={t("entryAiAlt")}
            width={36}
            height={36}
            className="hidden md:block m-0"
          />
          <h3
            ref={headingRef}
            className="text-xl font-bold text-gray-900 m-0"
            tabIndex={-1}
          >
            {processedTitle}
          </h3>
        </div>
        {processedDescription && (
          <p className="m-0 mt-4">{processedDescription}</p>
        )}
        {section.selectionType === "single" && (
          <p className="sr-only">
            Selecting an option will automatically move you to the next step.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-4 ">
        {isLoadingOptions ? (
          <SkeletonLoader
            variant={section.optionsVariant || "basic"}
            count={section.optionsVariant === "basic" ? 10 : 6}
            sectionTitle={sectionTitle}
          />
        ) : (
          <OptionsContainer
            options={displayedOptions}
            selectedOptions={selectedOptions}
            variant={section.optionsVariant || "basic"}
            showAllOptions={showAllOptions}
            onToggle={handleOptionToggle}
            selectionType={section.selectionType}
            sectionTitle={processedTitle}
          />
        )}
      </div>

      {optionsDisplayMode === "search" &&
        dynamicOptions &&
        dynamicOptions.length > 10 && (
          <div className="w-full relative" ref={dropdownRef}>
            <input
              type="text"
              placeholder={tCommon("searchOptions")}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setShowDropdown(e.target.value.length > 0);
              }}
              onFocus={() => searchText.length > 0 && setShowDropdown(true)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {showDropdown && filteredDropdownOptions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredDropdownOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleDropdownSelect(option)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-b-0 focus:outline-none focus:bg-gray-100 no-underline"
                  >
                    <div className="font-medium text-gray-900">
                      {option.label}
                    </div>
                    {option.description && (
                      <div className="text-sm text-gray-600 mt-1">
                        {option.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {showDropdown && filteredDropdownOptions.length === 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg px-4 py-3 text-gray-500">
                No options found
              </div>
            )}
          </div>
        )}
      {optionsDisplayMode === "expand" &&
        dynamicOptions &&
        dynamicOptions.length > 10 && (
          <ShowAllButton
            ref={showAllOptions ? showLessRef : showAllRef}
            totalCount={dynamicOptions.length}
            isExpanded={showAllOptions}
            onToggle={() => {
              const eventName = showAllOptions
                ? "show-less-options"
                : "show-all-options";
              const eventText = showAllOptions
                ? "Show less options"
                : "Show all options";
              fireElementClicked(eventName, eventText);
              setShowAllOptions(!showAllOptions);
            }}
          />
        )}

      {/* Navigation and Pagination */}
      <div className="flex flex-col gap-4 md:gap-0">
        {/* Buttons Row - Desktop: 3-col grid, Mobile: flex row */}
        <div className="flex justify-between md:grid md:grid-cols-3 md:items-center">
          {/* Back Button */}
          <div>
            {currentSectionIndex > 0 ? (
              <Button
                onClick={handleBack}
                className="not-prose px-6 py-3 rounded-full bg-transparent text-gray-900 border-none"
              >
                <ArrowLeftIcon /> {tCommon("back")}
              </Button>
            ) : (
              <div></div>
            )}
          </div>

          {/* Pagination Tracker - Centered on desktop, hidden on mobile */}
          <PaginationDots
            total={totalSections}
            current={currentSectionIndex}
            desktopOnly
          />

          {/* Next Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleNext}
              disabled={selectedOptions.length === 0 || isLoadingOptions}
              className="not-prose px-6 py-3 rounded-full bg-transparent text-gray-900 border-gray-900"
            >
              {isLoadingOptions
                ? t("loading")
                : currentSectionIndex === totalSections - 1
                  ? t("seeMyResults")
                  : tCourse("continue")}
            </Button>
          </div>
        </div>

        {/* Pagination Tracker - Below buttons on mobile, hidden on desktop */}
        <PaginationDots
          total={totalSections}
          current={currentSectionIndex}
          mobileOnly
        />
      </div>
    </div>
  );
};

export default MultipleChoice;
